import type { FC } from 'react';
import React, { useContext, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { Inline } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import AddIcon from '@atlaskit/icon/core/add';
import CommentIcon from '@atlaskit/icon/core/comment';
import ClockIcon from '@atlaskit/icon/core/clock';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useBulkIssueCreate } from '@confluence/bulk-issue-create/entry-points/useBulkIssueCreate.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { CommentCountsContext } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	WarningDialog,
	useWarningDialogStateActions,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialogContextProvider.ts';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';

import { PopupStateTypes } from './popupStateTypes.ts';

export const i18n = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	audioSummarySectionLabel: {
		id: 'ai-floating-context-menu.menu.item.label.audio-summary',
		defaultMessage: 'Listen to Page Summary',
		description: 'Label for the audio summary menu item',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	betaLabel: {
		id: 'ai-floating-context-menu.menu.item.lozenge.beta',
		defaultMessage: 'Beta',
		description:
			'Text for the Beta lozenge that appears to the right for action menu items that are in beta phase',
	},
	summarizeSectionLabel: {
		id: 'ai-floating-context-menu.summarize.section.label',
		defaultMessage: 'Summarize page',
		description: 'Label for the summarize section in the menu group',
	},
	createSectionLabel: {
		id: 'ai-floating-context-menu.create.section.label',
		defaultMessage: 'Create',
		description: 'Label for the create section in the menu group',
	},
});

enum ContentActionsMenuSectionKey {
	Summarize = 'Summarize',
	Create = 'Create',
}

type ContentActionsMenuSection = {
	key: ContentActionsMenuSectionKey;
	label: string;
	showSection: boolean;
};

type ContentActionsMenuItemType = {
	key: PopupStateTypes;
	label: string;
	showMenuItem: boolean;
	isDisabled: boolean;
	beta: boolean;
	section: ContentActionsMenuSectionKey;
	icon?: React.ReactNode;
};

type AIFloatingContextMenuGroupProps = {
	contentId: string;
	isLivePage?: boolean;
	setIsOpen: (state: boolean) => void;
	setPopupState: (popupKey: PopupStateTypes) => void;
};

export const AIFloatingContextMenuGroup: FC<AIFloatingContextMenuGroupProps> = ({
	contentId,
	isLivePage = false,
	setIsOpen,
	setPopupState,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const { edition } = useSessionData();
	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();

	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
	});

	const menuItems: ContentActionsMenuItemType[] = [
		{
			key: PopupStateTypes.QuickSummary,
			label: intl.formatMessage(i18n.summarizeSectionLabel),
			showMenuItem: true,
			isDisabled: false,
			beta: false,
			section: ContentActionsMenuSectionKey.Summarize,
			icon: <ListBulletedIcon label="" />,
		},
		{
			key: PopupStateTypes.PageCatchup,
			label: intl.formatMessage(i18n.pageCatchupMenuItemLabel),
			showMenuItem: true,
			isDisabled: false,
			beta: false,
			section: ContentActionsMenuSectionKey.Summarize,
			icon: <ClockIcon label="" />,
		},
		{
			key: PopupStateTypes.Comments,
			label: intl.formatMessage(i18n.inlineCommentsMenuItemLabel),
			showMenuItem: true,
			isDisabled: isLivePage
				? unresolvedInlineCommentsCount === 0
				: unresolvedInlineCommentsCount === 0 && footerCommentsCount === 0,
			beta: false,
			section: ContentActionsMenuSectionKey.Summarize,
			icon: <CommentIcon label="" />,
		},
		{
			key: PopupStateTypes.BulkIssueCreate,
			label: intl.formatMessage(
				fg('confluence-issue-terminology-refresh')
					? i18n.bulkWorkItemsCreateMenuItemLabel
					: i18n.bulkIssueCreateMenuItemLabel,
			),
			showMenuItem: fg('confluence_ai_bulk_issue_creation'),
			isDisabled: false,
			beta: false,
			section: ContentActionsMenuSectionKey.Create,
			icon: <AddIcon label="" />,
		},
	];

	const sectionItems: ContentActionsMenuSection[] = [
		{
			key: ContentActionsMenuSectionKey.Summarize,
			label: intl.formatMessage(i18n.summarizeSectionLabel),
			showSection: menuItems.some((item) => item.section === 'Summarize' && item.showMenuItem),
		},
		{
			key: ContentActionsMenuSectionKey.Create,
			label: intl.formatMessage(i18n.createSectionLabel),
			showSection:
				!isLivePage && menuItems.some((item) => item.section === 'Create' && item.showMenuItem),
		},
	];

	const onMenuItemSelection = (key: PopupStateTypes) => {
		let additionalAnalyticsAttributes = {};

		if (key === PopupStateTypes.Comments) {
			additionalAnalyticsAttributes = {
				numPageCommentsSummarized: footerCommentsCount,
				numInlineCommentsSummarized: unresolvedInlineCommentsCount,
			};
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'clicked',
				actionSubject: 'menuItem',
				actionSubjectId: `aiActionsMenuItem-${key}`,
				attributes: {
					contentId,
					edition,
					...additionalAnalyticsAttributes,
					isLivePage,
				},
			},
		}).fire();

		if (key === PopupStateTypes.BulkIssueCreate && fg('confluence_ai_bulk_issue_creation')) {
			if (isSidePanelOpen) {
				showWarningDialog(WarningDialog.FLOATING);
			} else {
				setIsOpen(false);
				void handleBulkIssueCreate();
			}
		} else {
			setPopupState(key);
		}
	};

	return (
		<MenuGroup testId="ai-uber-button-menu-group">
			{sectionItems.map((section) =>
				section.showSection ? (
					<Section key={section.key} title={section.label}>
						{menuItems.map((item) =>
							item.showMenuItem && item.section === section.key ? (
								<ButtonItem
									iconBefore={item.icon}
									key={item.key}
									isDisabled={item.isDisabled}
									onClick={() => {
										onMenuItemSelection(item.key);
									}}
								>
									<Inline spread="space-between" alignBlock="center">
										{item.label}
										{item.beta && <Lozenge>{intl.formatMessage(i18n.betaLabel)}</Lozenge>}
									</Inline>
								</ButtonItem>
							) : null,
						)}
					</Section>
				) : null,
			)}
		</MenuGroup>
	);
};
